import React, { forwardRef, useState, useRef, useEffect, CSSProperties } from "react";

export interface LanguageValidationProps {
  isValid: (char: string) => boolean;
  lang: string;
}

interface LanguageValidatedInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  languageValidation: LanguageValidationProps;
}

export const InputComponent = forwardRef<HTMLInputElement, LanguageValidatedInputProps>(
  ({ languageValidation, className = "", style, ...props }, ref) => {
    const [isComposing, setIsComposing] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const lastValidValueRef = useRef(props.value as string);

    const defaultStyle: CSSProperties = {
      padding: "8px 12px",
      fontSize: "16px",
      border: "1px solid #ccc",
      borderRadius: "4px",
      width: "100%",
      boxSizing: "border-box",
    };

    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.value = lastValidValueRef.current;
      }
    }, [languageValidation]);

    const validateInput = (value: string): boolean => {
      return value.split("").every((char) => languageValidation.isValid(char) || char === " ");
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;

      if (!isComposing) {
        const isValid = validateInput(newValue);

        if (isValid) {
          lastValidValueRef.current = newValue;
          if (props.onChange) {
            props.onChange(e);
          }
        } else {
          // Reset to last valid value
          e.target.value = lastValidValueRef.current;
          if (props.onChange) {
            props.onChange({
              ...e,
              target: { ...e.target, value: lastValidValueRef.current },
            });
          }
        }
      } else if (props.onChange) {
        props.onChange(e);
      }
    };

    const handleCompositionStart = () => {
      setIsComposing(true);
    };

    const handleCompositionEnd = (e: React.CompositionEvent<HTMLInputElement>) => {
      setIsComposing(false);
      const value = e.currentTarget.value;

      const isValid = validateInput(value);

      if (isValid) {
        lastValidValueRef.current = value;
        if (props.onChange) {
          props.onChange({ target: { value } } as React.ChangeEvent<HTMLInputElement>);
        }
      } else {
        // Reset to last valid value
        e.currentTarget.value = lastValidValueRef.current;
        if (props.onChange) {
          props.onChange({ target: { value: lastValidValueRef.current } } as React.ChangeEvent<HTMLInputElement>);
        }
      }
    };

    return (
      <input
        {...props}
        ref={(node) => {
          inputRef.current = node;
          if (typeof ref === "function") {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        onChange={handleInputChange}
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
        lang={languageValidation.lang}
        className={`border border-gray-300 rounded-md p-2 ${className}`}
        style={{ ...defaultStyle, ...style }}
      />
    );
  }
);

InputComponent.displayName = "InputComponent";
